import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  keepPreviousData,
} from '@tanstack/react-query';
import * as membersApi from '@/api/members';
import type { MemberFilters, MemberFormData } from '@/types/member';

const MEMBERS_KEY = ['members'] as const;

export function useMembers(filters?: MemberFilters) {
  return useInfiniteQuery({
    queryKey: [...MEMBERS_KEY, 'list', filters],
    queryFn: ({ pageParam = 1 }) =>
      membersApi.getMembers({ ...filters, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next_page_url ? (lastPageParam as number) + 1 : undefined,
  });
}

export function useMembersList(filters?: MemberFilters) {
  return useQuery({
    queryKey: [...MEMBERS_KEY, 'flat-list', filters],
    queryFn: () => membersApi.getMembers(filters),
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useMember(id: string | undefined) {
  return useQuery({
    queryKey: [...MEMBERS_KEY, 'detail', id],
    queryFn: () => membersApi.getMember(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MemberFormData) => membersApi.createMember(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MEMBERS_KEY });
    },
  });
}

export function useUpdateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<MemberFormData>;
    }) => membersApi.updateMember(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...MEMBERS_KEY, 'detail', variables.id],
      });
      void queryClient.invalidateQueries({
        queryKey: [...MEMBERS_KEY, 'flat-list'],
      });
    },
  });
}

export function useDeleteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => membersApi.deleteMember(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MEMBERS_KEY });
    },
  });
}

export function useChangeStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof membersApi.changeStatus>[1]) =>
      membersApi.changeStatus(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...MEMBERS_KEY, 'detail', variables.id],
      });
      void queryClient.invalidateQueries({
        queryKey: [...MEMBERS_KEY, 'flat-list'],
      });
    },
  });
}

export function useApproveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => membersApi.approveMember(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({
        queryKey: [...MEMBERS_KEY, 'detail', id],
      });
      void queryClient.invalidateQueries({ queryKey: MEMBERS_KEY });
    },
  });
}

export function useMemberNotes(id: string | undefined) {
  return useQuery({
    queryKey: [...MEMBERS_KEY, 'notes', id],
    queryFn: () => membersApi.getMemberNotes(id!),
    enabled: !!id,
  });
}

export function useAddMemberNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      membersApi.addMemberNote(id, content),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...MEMBERS_KEY, 'notes', variables.id],
      });
    },
  });
}

export function useUpdateMemberNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, noteId, content }: { memberId: string; noteId: string; content: string }) =>
      membersApi.updateMemberNote(memberId, noteId, content),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...MEMBERS_KEY, 'notes', variables.memberId],
      });
    },
  });
}

export function useDeleteMemberNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, noteId }: { memberId: string; noteId: string }) =>
      membersApi.deleteMemberNote(memberId, noteId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...MEMBERS_KEY, 'notes', variables.memberId],
      });
    },
  });
}

export function useMemberHistory(id: string | undefined) {
  return useQuery({
    queryKey: [...MEMBERS_KEY, 'history', id],
    queryFn: () => membersApi.getMemberHistory(id!),
    enabled: !!id,
  });
}

export function useUploadPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      membersApi.uploadPhoto(id, file),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...MEMBERS_KEY, 'detail', variables.id],
      });
    },
  });
}
